import React, { useCallback, useEffect, useState } from 'react';
import ApplicationForm from './ApplicationForm';
import ApplicationDetailView from './ApplicationDetailView';
import { tradeLicenseApi } from '../../services/tradeLicenseApi';

const ApplicantDashboard = ({ applicant, categories, onLogout }) => {
  const [view, setView] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [clarificationText, setClarificationText] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'transfer',
    reference: '',
    remarks: '',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showNewApp, setShowNewApp] = useState(false);
  const [renewPrefill, setRenewPrefill] = useState(null);
  const [renewingLicense, setRenewingLicense] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [appRes, licRes] = await Promise.all([
        tradeLicenseApi.getMyApplications({ page_size: 50 }),
        tradeLicenseApi.getMyLicenses({ page_size: 50 }),
      ]);
      setApplications(appRes.data.results || appRes.data || []);
      setLicenses(licRes.data.results || licRes.data || []);
    } catch (err) {
      setError('Failed to load your records.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openApplication = async (app) => {
    setError('');
    setMessage('');
    setSelectedApp(null);
    try {
      const res = await tradeLicenseApi.getMyApplicationDetail(app.sid || app.id);
      setSelectedApp(res.data);
      const fee = parseFloat(res.data.application_fee || 0);
      const paid = parseFloat(res.data.paid_amount || 0);
      const pending = parseFloat(res.data.pending_payment_amount || 0);
      const balance = Math.max(0, fee - paid - pending);
      setPaymentForm((prev) => ({ ...prev, amount: balance > 0 ? String(balance) : '' }));
    } catch (err) {
      setError('Could not load application details.');
    }
  };

  const handleClarification = async () => {
    if (!selectedApp || !clarificationText.trim()) return;
    setActionLoading(true);
    setError('');
    try {
      await tradeLicenseApi.respondClarification(selectedApp.sid || selectedApp.id, clarificationText.trim());
      setMessage('Clarification submitted successfully.');
      setClarificationText('');
      await openApplication(selectedApp);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit clarification.');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedApp) return;
    setActionLoading(true);
    setError('');
    try {
      await tradeLicenseApi.submitApplicantPayment(selectedApp.sid || selectedApp.id, paymentForm);
      setMessage('Payment details submitted for verification.');
      await openApplication(selectedApp);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit payment.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitDraft = async (app) => {
    const appRef = app.sid || app.id;
    setActionLoading(true);
    try {
      await tradeLicenseApi.submitApplication(appRef);
      setMessage('Application submitted successfully.');
      fetchData();
      if (selectedApp && (selectedApp.sid || selectedApp.id) === appRef) {
        await openApplication(app);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit application.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenewAsIs = async (lic) => {
    const ok = window.confirm(
      `Renew license ${lic.license_number} with the same details?\n` +
      'Your renewal application will be submitted directly — no form to fill.'
    );
    if (!ok) return;
    setRenewingLicense(lic.license_number);
    setError('');
    setMessage('');
    try {
      const res = await tradeLicenseApi.renewAsIs(lic.license_number);
      setMessage(
        `Renewal application ${res.data.application_number} submitted successfully. ` +
        'Track it under My Applications.'
      );
      setView('applications');
      setSelectedApp(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit renewal.');
    } finally {
      setRenewingLicense(null);
    }
  };

  const handleRenewModify = async (lic) => {
    setError('');
    setMessage('');
    try {
      const res = await tradeLicenseApi.lookupLicense(lic.license_number);
      if (!res.data.prefill) {
        setError('License details could not be pre-filled. Please contact the office.');
        return;
      }
      setRenewPrefill(res.data.prefill);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load license details.');
    }
  };

  if (renewPrefill) {
    return (
      <div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRenewPrefill(null)}>
          ← Back to Dashboard
        </button>
        <div className="renewal-mode-banner">
          <p>Modify details as needed before submitting renewal of {renewPrefill.existing_license_number}.</p>
        </div>
        <ApplicationForm
          categories={categories}
          initialData={renewPrefill}
          lockApplicationType
          readOnlyFields={['existing_license_number']}
          onSuccess={(data) => {
            setRenewPrefill(null);
            setView('applications');
            fetchData();
            setMessage(`Renewal application ${data.application_number || ''} created successfully.`);
          }}
        />
      </div>
    );
  }

  if (showNewApp) {
    return (
      <div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowNewApp(false)}>
          ← Back to Dashboard
        </button>
        <ApplicationForm
          categories={categories}
          initialData={{
            applicant_phone: applicant.phone,
            applicant_email: applicant.email || '',
            applicant_first_name_en: applicant.first_name || '',
            applicant_last_name_en: applicant.last_name || '',
          }}
          onSuccess={() => {
            setShowNewApp(false);
            fetchData();
            setMessage('Application created successfully.');
          }}
        />
      </div>
    );
  }

  return (
    <div className="applicant-dashboard">
      <div className="dashboard-top">
        <div>
          <h2>My Account</h2>
          <p>Welcome, {applicant.full_name || applicant.phone}</p>
        </div>
        <div className="dashboard-actions">
          <button type="button" className="btn btn-primary" onClick={() => setShowNewApp(true)}>
            + New Application
          </button>
          <button type="button" className="btn btn-secondary" onClick={onLogout}>
            Logout
          </button>
        </div>
      </div>

      <div className="dashboard-tabs">
        <button
          type="button"
          className={`tab-btn ${view === 'applications' ? 'active' : ''}`}
          onClick={() => { setView('applications'); setSelectedApp(null); }}
        >
          My Applications
        </button>
        <button
          type="button"
          className={`tab-btn ${view === 'licenses' ? 'active' : ''}`}
          onClick={() => { setView('licenses'); setSelectedApp(null); }}
        >
          My Licenses
        </button>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading">Loading your records…</div>
      ) : view === 'licenses' ? (
        <div className="dashboard-list">
          {licenses.length === 0 ? (
            <p className="empty-msg">No licenses issued yet.</p>
          ) : (
            licenses.map((lic) => (
              <div key={lic.license_number || lic.id} className="dashboard-card">
                <h3>{lic.business_name || lic.business_name_en}</h3>
                <p><strong>{lic.license_number}</strong></p>
                <p>Valid till: {lic.valid_till ? new Date(lic.valid_till).toLocaleDateString() : '—'}</p>
                <div className="license-card-actions">
                  <a
                    href={`/trade-license/certificate/${lic.license_number}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary btn-sm"
                  >
                    View Certificate
                  </a>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    disabled={renewingLicense === lic.license_number}
                    onClick={() => handleRenewAsIs(lic)}
                  >
                    {renewingLicense === lic.license_number ? 'Submitting…' : '🔄 Renew (Same Details)'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleRenewModify(lic)}
                  >
                    ✏️ Renew with Changes
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : selectedApp ? (
        <div className="application-detail-panel">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSelectedApp(null)}>
            ← Back to list
          </button>
          <ApplicationDetailView application={selectedApp} />

          {selectedApp.clarification_required && (
            <div className="action-panel">
              <h4>Clarification Required</h4>
              <p>{selectedApp.clarification_reason_en}</p>
              <textarea
                rows={4}
                value={clarificationText}
                onChange={(e) => setClarificationText(e.target.value)}
                placeholder="Your response…"
              />
              <button
                type="button"
                className="btn btn-primary"
                disabled={actionLoading}
                onClick={handleClarification}
              >
                Submit Response
              </button>
            </div>
          )}

          {['approved', 'paid'].includes(selectedApp.status) &&
            Number(selectedApp.paid_amount || 0) < Number(selectedApp.application_fee || 0) && (
            <div className="action-panel">
              <h4>Submit Payment Details</h4>
              <p>Pay at counter or submit cheque/bank transfer details for verification.</p>
              <div className="form-row-2">
                <input
                  type="number"
                  placeholder="Amount"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                />
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm((p) => ({ ...p, payment_method: e.target.value }))}
                >
                  <option value="transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <input
                type="text"
                placeholder="Reference / Cheque No."
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm((p) => ({ ...p, reference: e.target.value }))}
              />
              <button
                type="button"
                className="btn btn-primary"
                disabled={actionLoading}
                onClick={handlePayment}
              >
                Submit Payment
              </button>
            </div>
          )}

          {selectedApp.status === 'draft' && (
            <button
              type="button"
              className="btn btn-success"
              disabled={actionLoading}
              onClick={() => handleSubmitDraft(selectedApp)}
            >
              Submit Application
            </button>
          )}

          {selectedApp.status === 'issued' && selectedApp.license_number && (
            <a
              href={`/trade-license/certificate/${selectedApp.license_number}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
            >
              View Certificate
            </a>
          )}
        </div>
      ) : (
        <div className="dashboard-list">
          {applications.length === 0 ? (
            <p className="empty-msg">No applications found. Start a new application above.</p>
          ) : (
            applications.map((app) => (
              <div key={app.id} className="dashboard-card clickable" onClick={() => openApplication(app)}>
                <div className="dashboard-card-head">
                  <h3>{app.business_name_en}</h3>
                  <span className={`status-badge ${app.status}`}>{app.status}</span>
                </div>
                <p>{app.application_number}</p>
                <p>{app.current_stage_name || '—'}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ApplicantDashboard;
